import type Phaser from 'phaser';
import { activePhysicsIsolationPreset, resolveAnimalPhysics } from '../physicsIsolationPresets';
import { markBodyMaterialType } from '../logic/contactMaterial';
import { applyAnimalCenterOfMass } from './centerOfMass';
import { hitboxMaskCollisionParts } from './hitboxMaskRecipes';
import { ANIMAL_SIZE_SCALE, type AnimalDefinition, type CollisionPart } from './types';

export type WorldPartDescriptor = CollisionPart & { x: number; y: number };

export interface MatterBodyFactories {
  bodies: {
    circle(x: number, y: number, radius: number, options?: MatterJS.IBodyDefinition): MatterJS.BodyType;
    rectangle(x: number, y: number, width: number, height: number, options?: MatterJS.IBodyDefinition): MatterJS.BodyType;
    fromVertices(
      x: number,
      y: number,
      vertexSets: MatterJS.Vector[][],
      options?: MatterJS.IBodyDefinition,
    ): MatterJS.BodyType;
  };
  body: {
    create(options?: MatterJS.IBodyDefinition): MatterJS.BodyType;
    setDensity(body: MatterJS.BodyType, density: number): void;
    setCentre(body: MatterJS.BodyType, centre: MatterJS.Vector, relative?: boolean): void;
  };
}

export const AIMING_COLLISION_MASK = 0;
export const NORMAL_ANIMAL_COLLISION_MASK = 0xffffffff;

export function animalTextureKey(animal: AnimalDefinition): string {
  return `animal:${animal.id}`;
}

export function partWorldDescriptors(
  animal: AnimalDefinition,
  worldX: number,
  worldY: number,
): WorldPartDescriptor[] {
  return collisionPartsForAnimal(animal).map((part) => ({ ...part, x: worldX + part.x, y: worldY + part.y }));
}

export function collisionPartsForAnimal(animal: AnimalDefinition): readonly CollisionPart[] {
  let parts: readonly CollisionPart[] = animal.parts;
  if (animal.hitboxMask) {
    const maskParts = hitboxMaskCollisionParts(animal.hitboxMask.path);
    if (maskParts) parts = maskParts;
  }
  return parts.map((part): CollisionPart => {
    const base = {
      ...part,
      x: part.x * ANIMAL_SIZE_SCALE,
      y: part.y * ANIMAL_SIZE_SCALE,
    };
    if (part.kind === 'circle') return { ...base, kind: 'circle', radius: part.radius * ANIMAL_SIZE_SCALE };
    if (part.kind === 'rectangle') {
      return {
        ...base,
        kind: 'rectangle',
        width: part.width * ANIMAL_SIZE_SCALE,
        height: part.height * ANIMAL_SIZE_SCALE,
      };
    }
    return {
      ...base,
      kind: 'polygon',
      vertices: part.vertices.map(({ x, y }) => ({ x: x * ANIMAL_SIZE_SCALE, y: y * ANIMAL_SIZE_SCALE })),
    };
  });
}

export function createAnimal(
  scene: Phaser.Scene,
  animal: AnimalDefinition,
  x: number,
  y: number,
): Phaser.Physics.Matter.Image {
  const physicsPreset = activePhysicsIsolationPreset();
  const compound = createAnimalMatterBody({ bodies: scene.matter.bodies, body: scene.matter.body }, animal, physicsPreset);
  const visualCenter = animal.visualCenter ?? { x: 0, y: 0 };
  const visualOriginX = 0.5 + (compound.position.x - visualCenter.x) / animal.width;
  const visualOriginY = 0.5 + (compound.position.y - visualCenter.y) / animal.height;
  const frame = animal.texture?.kind === 'atlas' ? animal.texture.frame : undefined;
  const image = scene.matter.add.image(x, y, animalTextureKey(animal), frame);
  image.setDisplaySize(animal.width, animal.height);
  image.setExistingBody(compound);
  image.setOrigin(visualOriginX, visualOriginY);
  image.setPosition(x, y);
  image.setDensity(animal.density);
  image.setStatic(true);
  image.setCollidesWith(AIMING_COLLISION_MASK);
  image.setData({ animalId: animal.id, dropped: false, placed: false });
  return image;
}

export function createAnimalMatterBody(
  matter: MatterBodyFactories,
  animal: AnimalDefinition,
  preset: Parameters<typeof resolveAnimalPhysics>[1],
): MatterJS.BodyType {
  const physics = resolveAnimalPhysics(animal, preset);
  const partBodies = partWorldDescriptors(animal, 0, 0).flatMap((part) => {
    const options = part.id ? { label: `animal:${animal.id}:${part.id}` } : undefined;
    let body: MatterJS.BodyType;
    if (part.kind === 'circle') {
      body = matter.bodies.circle(part.x, part.y, part.radius, options);
    } else if (part.kind === 'polygon') {
      body = matter.bodies.fromVertices(part.x, part.y, [[...part.vertices]], options);
    } else {
      body = matter.bodies.rectangle(part.x, part.y, part.width, part.height, {
        ...options,
        angle: part.angle ?? 0,
      });
    }
    return body.parts.length > 1 ? body.parts.slice(1) : [body];
  });
  const compound = matter.body.create({
    label: `animal:${animal.id}`,
    parts: partBodies,
    ...physics,
  });
  markBodyMaterialType(compound, 'animal');
  // Matter recomputes compound mass properties from the default-density child
  // bodies while assembling `parts`, so apply the roster density afterwards.
  matter.body.setDensity(compound, physics.density);
  applyAnimalCenterOfMass(matter.body, compound, animal);
  return compound;
}

export function enableAnimalCollisions(image: Phaser.Physics.Matter.Image): void {
  image.setCollidesWith(NORMAL_ANIMAL_COLLISION_MASK);
}
